import type { TargetAndTransition, Transition } from 'framer-motion';

export const DURATIONS = {
  instant: 0.1,
  fast: 0.2,
  normal: 0.3,
  slow: 0.5,
  slower: 0.7,
  slowest: 1,
} as const;

export const LONG_DURATIONS = {
  spinner: 1,
  quickFloat: 3,
  float: 4,
  floatSlow: 5,
  blob: 8,
  blobSlow: 10,
} as const;

export const EASINGS = {
  soft: [0.4, 0, 0.2, 1],
  bouncy: [0.68, -0.55, 0.265, 1.55],
  smooth: [0.25, 0.1, 0.25, 1],
  snappy: [0.4, 0, 0.6, 1],
  gentle: [0.22, 1, 0.36, 1],
  out: [0.4, 0, 1, 1],
  linear: 'linear',
  easeInOut: 'easeInOut',
} as const;

export const TRANSITIONS: Record<string, Transition> = {
  default: {
    duration: DURATIONS.normal,
    ease: EASINGS.soft,
  },
  fast: {
    duration: DURATIONS.fast,
    ease: EASINGS.snappy,
  },
  slow: {
    duration: DURATIONS.slow,
    ease: EASINGS.gentle,
  },
  spring: {
    type: 'spring',
    stiffness: 300,
    damping: 30,
    mass: 1,
  },
  springBouncy: {
    type: 'spring',
    stiffness: 400,
    damping: 25,
  },
  springGentle: {
    type: 'spring',
    stiffness: 200,
    damping: 30,
  },
  smooth: {
    duration: DURATIONS.normal,
    ease: EASINGS.soft,
  },
  bouncy: {
    duration: DURATIONS.slow,
    ease: EASINGS.bouncy,
  },
} as const;

export const VIEWPORT = {
  once: {
    once: true,
    margin: '-50px',
    amount: 0.2,
  },
  repeat: {
    once: false,
    margin: '-100px',
    amount: 0.3,
  },
} as const;

export const viewportOnce = VIEWPORT.once;

export const DECORATIVE_ANIMATIONS: Record<string, Record<string, TargetAndTransition>> = {
  spinner: {
    rotate: {
      rotate: 360,
      transition: {
        duration: LONG_DURATIONS.spinner,
        repeat: Infinity,
        ease: EASINGS.linear,
      },
    },
    pulse: {
      scale: [1, 1.05, 1],
      opacity: [1, 0.8, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: EASINGS.easeInOut,
      },
    },
  },
  float: {
    slow: {
      scale: [1, 1.2, 1],
      opacity: [0.3, 0.5, 0.3],
      transition: {
        duration: LONG_DURATIONS.blob,
        repeat: Infinity,
        ease: EASINGS.easeInOut,
      },
    },
    slower: {
      scale: [1, 1.3, 1],
      opacity: [0.3, 0.6, 0.3],
      transition: {
        duration: LONG_DURATIONS.blobSlow,
        repeat: Infinity,
        ease: EASINGS.easeInOut,
        delay: 2,
      },
    },
    fast: {
      rotateX: [60, 58, 60],
      scale: [1, 1.05, 1],
      transition: {
        duration: LONG_DURATIONS.float,
        repeat: Infinity,
        ease: EASINGS.easeInOut,
      },
    },
  },
  bounce: {
    soft: {
      y: [0, -20, 0],
      rotate: [0, 5, -5, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: EASINGS.easeInOut,
      },
    },
    gentle: {
      y: [0, -10, 0],
      transition: {
        duration: LONG_DURATIONS.float,
        repeat: Infinity,
        ease: EASINGS.easeInOut,
      },
    },
  },
} as const;
